import React, { useState } from 'react';
import Button from './Button';
import { InfoCircle } from 'iconoir-react';
import { InfoTip } from './InfoTip';
import type { TooltipKey } from '../../tooltips/copy';

interface TabGroupProps {
  className?: string;
  tabClassName?: string;
  activeTabClassName?: string;
  tabs: {
    label: string;
    value: string;
    count?: number;
    /** Jun-18 (a user V1 test): optional per-tab info tooltip. When set, an ⓘ
     *  renders inside the tab and hovering it explains what the tab means. */
    tooltipKey?: TooltipKey;
  }[];
  activeTab: string;
  tabSize?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  onChange: (tab: string) => void;
}

const TabGroup = (props: TabGroupProps) => {
  const {
    className,
    tabClassName,
    activeTabClassName,
    tabs,
    activeTab,
    tabSize = 'md',
    onChange,
  } = props;
  const [active, setActive] = useState(activeTab);

  const handleClick = (tab: string) => {
    setActive(tab);
    onChange(tab);
  };

  return (
    <div
      className={`flex items-center gap-1 sm:gap-2 mb-6 p-1 bg-gray-light-100 rounded-xl shadow-5xl overflow-x-auto scrollbar-hide ${className}`}
    >
      {tabs.map(tab => (
        <Button
          key={tab.value}
          onClick={() => handleClick(tab.value)}
          variant={active === tab.value ? 'solid' : 'plain'}
          className={`${active === tab.value ? `bg-white !text-heading-500 hover:!text-white border-white hover:!border-primary-500 shadow-sm ${activeTabClassName}` : `!text-body-100 hover:!text-white ${tabClassName}`} w-full !font-bold`}
          size={tabSize}
        >
          {tab.label}
          {tab.count !== undefined && tab.count !== null && (
            <span
              className={`text-sm text-body-500 min-w-5 h-5 flex items-center justify-center rounded-sm bg-white`}
            >
              {tab.count}
            </span>
          )}
          {tab.tooltipKey && (
            // Jun-18 (a user V1 test): info tooltip inside the tab. Wraps an
            // InfoCircle (not InfoTip's own button) to avoid a nested <button>;
            // stopPropagation so tapping the ⓘ doesn't switch tab.
            <span
              className='inline-flex items-center'
              onClick={e => e.stopPropagation()}
            >
              <InfoTip tooltipKey={tab.tooltipKey} side='bottom'>
                <InfoCircle className='w-3.5 h-3.5 opacity-70' />
              </InfoTip>
            </span>
          )}
        </Button>
      ))}
    </div>
  );
};

export default TabGroup;
